/**
 * @file MeetingNotesDocView.tsx
 * @brief Meeting Notes v2 on the web: overview, topic sections, next steps
 *        and "Also in your notes", every line clickable for its evidence.
 *
 * @details
 * DES 09-10-2026, the web twin of the phone's MeetingNotesDocView
 * (07-10-2026, docs/meeting-notes-v2-plan.md). Same content, order, labels
 * and words:
 *   - Overview: one or two sentences.
 *   - A section per topic, in conversation order: bullets with their
 *     sub-points (◦), then the questions left open ("?").
 *   - Next steps: task, then owner and one line of why.
 *   - Also in your notes: typed points the meeting did not clearly cover
 *     (never "not discussed": that claim would be false for some).
 *
 * Points sit on the Notes timeline, the Note detail page's look
 * (NoteDetailPage Points: a number on a rail broken around it); each
 * section, Next steps and Also in your notes number from 1. A point the
 * owner typed leads its topic with a small "Your note" label and the pen
 * before the text, in the secondary colour; it is numbered and weighted like
 * every other lead bullet (DES 10-10-2026, as the app).
 *
 * Labels: `confirmed` is the default and carries no pill (a pill on every row
 * is noise); `suggestion`, `open` and `unclear` do.
 *
 * Evidence: mouse users click, they do not press and hold (DES 05-10-2026),
 * so a click on a line opens "where it was said" under it
 * (MeetingEvidenceInline), one at a time across the page (useEvidenceOpen).
 * Ids come from `flattenNotesDoc` only, so each row finds its own anchor.
 *
 * @references
 * - FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/components/MeetingNotesDocView.tsx
 * - ../notesDoc.ts (types, flattenNotesDoc)
 * - MeetingNotesTierView.tsx (mounts it when the meeting has a notes doc)
 *
 * @author DES
 * @date 09-10-2026
 */

import type { ReactNode } from "react";
import { PenLine } from "lucide-react";
import { TYPED_NOTE_LABEL } from "../typedNote";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";
import type { MeetingDetail } from "../schemas";
import {
  flattenNotesDoc,
  type NotesDoc,
  type NotesDocAnchor,
  type NotesDocBullet,
  type NotesDocLabel,
} from "../notesDoc";
import { MeetingBlockHeading } from "./MeetingBlockHeading";
import { EvidenceCollapse, MeetingEvidenceInline } from "./MeetingEvidenceInline";
import { useEvidenceOpen } from "./evidenceOpen";
import { readStoredAnchor } from "./meetingEvidence";

const LABEL_TEXT: Record<Exclude<NotesDocLabel, "confirmed">, string> = {
  suggestion: "Suggestion",
  open: "Open",
  unclear: "Unclear audio",
};

const dirOf = (t: string): "rtl" | "ltr" => (isRTLText(t) ? "rtl" : "ltr");

// ----------------------------------------------------------------------
// Small pieces
// ----------------------------------------------------------------------
function LabelPill({ label }: { label: NotesDocLabel | null }) {
  if (!label || label === "confirmed") return null;
  return (
    <span className="rounded-full bg-black/[0.04] px-2 py-0.5 text-[11px] font-medium leading-[15px] text-app-text-secondary dark:bg-white/[0.06]">
      {LABEL_TEXT[label]}
    </span>
  );
}

/** Label pill and speaker under a line, when it has either. */
function Meta({ label, speaker }: { label: NotesDocLabel | null; speaker: string | null }) {
  const hasLabel = !!label && label !== "confirmed";
  if (!hasLabel && !speaker) return null;
  return (
    <span className="mt-[3px] flex flex-wrap items-center gap-1.5" dir="ltr">
      <LabelPill label={label} />
      {speaker ? <span className="text-xs text-app-text-secondary">{speaker}</span> : null}
    </span>
  );
}

const PenMark = () => <PenLine className="h-4 w-4 text-app-text" aria-label={TYPED_NOTE_LABEL} />;

/** "Your note" before a typed point (DES 10-10-2026): secondary, not brand. */
const TypedTag = () => (
  <span className="mb-0.5 flex items-center gap-1 text-xs font-medium leading-4 text-app-text-secondary">
    <PenLine className="h-[13px] w-[13px]" aria-hidden />
    {TYPED_NOTE_LABEL}
  </span>
);

/** The rail's number or "?", as NoteDetailPage's Points. */
const RailText = ({ children }: { children: ReactNode }) => (
  <span className="text-lg font-semibold leading-7 tabular-nums text-muted-foreground">
    {children}
  </span>
);

type TimelineItem = { key: string; mark: ReactNode; body: ReactNode };

/** The Notes points timeline (NoteDetailPage): a mark, one rail broken around it. */
function Timeline({ items }: { items: TimelineItem[] }) {
  return (
    <ol>
      {items.map((it, i) => (
        <li key={it.key} className="flex gap-3">
          <div className="flex w-8 shrink-0 flex-col items-center" aria-hidden>
            <span className={cn("h-1 w-0.5", i === 0 ? "bg-transparent" : "bg-border")} />
            <span className="flex h-7 items-center justify-center">{it.mark}</span>
            <span
              className={cn("w-0.5 flex-1", i === items.length - 1 ? "bg-transparent" : "bg-border")}
            />
          </div>
          <div className="min-w-0 flex-1 pb-[18px]">{it.body}</div>
        </li>
      ))}
    </ol>
  );
}

type EvidenceCtx = {
  meetingId: string;
  transcript: MeetingDetail["transcript"];
  recordingUrl: string | null | undefined;
};

/** One clickable line: click opens where it was said, under it. */
function EvidenceLine({
  ctx,
  id,
  anchor,
  children,
  className,
}: {
  ctx: EvidenceCtx;
  id: string;
  anchor: NotesDocAnchor | null;
  children: ReactNode;
  className?: string;
}) {
  const [open, toggle, close] = useEvidenceOpen(`${ctx.meetingId}:${id}`);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
      title={open ? undefined : "Show where this was said"}
      // Hover tint only while closed, as the Intel rows (DES 05-10-2026).
      className={cn(
        "-mx-2 cursor-pointer rounded-xl px-2 pb-[5px] pt-[3px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        !open && "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
        className,
      )}
    >
      {children}
      <EvidenceCollapse open={open}>
        <MeetingEvidenceInline
          anchor={readStoredAnchor(anchor)}
          rawAnchor={anchor}
          transcript={ctx.transcript}
          recordingUrl={ctx.recordingUrl}
          onClose={close}
          className="mb-1"
        />
      </EvidenceCollapse>
    </div>
  );
}

// ----------------------------------------------------------------------
// View
// ----------------------------------------------------------------------
type Props = {
  doc: NotesDoc;
  meeting: MeetingDetail;
};

export function MeetingNotesDocView({ doc, meeting }: Props) {
  const ctx: EvidenceCtx = {
    meetingId: meeting.id,
    transcript: meeting.transcript,
    recordingUrl: meeting.recording_url,
  };

  // Same numbering as the flatten: walk the doc once, hand out ids in order.
  const ids = flattenNotesDoc(doc).map((it) => it.id);
  let cursor = 0;
  const nextId = () => ids[cursor++] ?? `${cursor}`;

  const bulletItem = (b: NotesDocBullet, key: string, n: number): TimelineItem => {
    const id = nextId();
    const children = b.children ?? [];
    const childIds = children.map(() => nextId());
    return {
      key,
      mark: <RailText>{n}</RailText>,
      body: (
        <>
          <EvidenceLine ctx={ctx} id={id} anchor={b.anchor}>
            {b.typed ? <TypedTag /> : null}
            <p dir={dirOf(b.text)} className="text-base leading-[22px] text-app-text">
              {b.text}
            </p>
            <Meta label={b.label} speaker={b.speaker} />
          </EvidenceLine>
          {children.map((c, i) => (
            <EvidenceLine
              key={`${key}c${i}`}
              ctx={ctx}
              id={childIds[i] ?? `${key}c${i}`}
              anchor={c.anchor}
              className="mt-1"
            >
              <span className="flex items-start">
                <span className="w-4 shrink-0 text-sm leading-5 text-app-text-secondary" aria-hidden>
                  ◦
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    dir={dirOf(c.text)}
                    className="block text-sm leading-5 text-app-text-secondary"
                  >
                    {c.text}
                  </span>
                  <Meta label={c.label} speaker={c.speaker} />
                </span>
              </span>
            </EvidenceLine>
          ))}
        </>
      ),
    };
  };

  return (
    <div>
      {doc.overview ? (
        <p
          dir={dirOf(doc.overview)}
          className="mb-6 text-base font-medium leading-6 text-app-text"
        >
          {doc.overview}
        </p>
      ) : null}

      {doc.sections.map((s, si) => {
        // Typed points take the pen, not a number (the phone's count).
        let n = 0;
        const items: TimelineItem[] = [
          ...s.bullets.map((b, bi) => bulletItem(b, `s${si}b${bi}`, ++n)),
          ...s.openQuestions.map((q, qi) => {
            const id = nextId();
            return {
              key: `s${si}q${qi}`,
              mark: <RailText>?</RailText>,
              body: (
                <EvidenceLine ctx={ctx} id={id} anchor={q.anchor}>
                  <p dir={dirOf(q.text)} className="text-base leading-[22px] text-app-text">
                    {q.text}
                  </p>
                  <Meta label="open" speaker={null} />
                </EvidenceLine>
              ),
            };
          }),
        ];
        return (
          <section key={`s${si}`} className="mb-6">
            <MeetingBlockHeading>{s.heading}</MeetingBlockHeading>
            <Timeline items={items} />
          </section>
        );
      })}

      {doc.nextSteps.length ? (
        <section className="mb-6">
          <MeetingBlockHeading>Next steps</MeetingBlockHeading>
          <Timeline
            items={doc.nextSteps.map((st, i) => {
              const id = nextId();
              const sub = [st.owner, st.context].filter(Boolean).join(" · ");
              return {
                key: `n${i}`,
                mark: <RailText>{i + 1}</RailText>,
                body: (
                  <EvidenceLine ctx={ctx} id={id} anchor={st.anchor}>
                    <p dir={dirOf(st.task)} className="text-base leading-[22px] text-app-text">
                      {st.task}
                    </p>
                    {sub ? (
                      <p
                        dir={dirOf(sub)}
                        className="mt-0.5 text-[13px] leading-[18px] text-app-text-secondary"
                      >
                        {sub}
                      </p>
                    ) : null}
                  </EvidenceLine>
                ),
              };
            })}
          />
        </section>
      ) : null}

      {doc.alsoInYourNotes.length ? (
        <section className="mb-6">
          <MeetingBlockHeading>Also in your notes</MeetingBlockHeading>
          <Timeline
            items={doc.alsoInYourNotes.map((p, i) => ({
              key: `a${i}`,
              mark: <PenMark />,
              body: (
                <p
                  dir={dirOf(p)}
                  className="pt-[3px] text-base font-semibold leading-[22px] text-app-text"
                >
                  {p}
                </p>
              ),
            }))}
          />
        </section>
      ) : null}
    </div>
  );
}

export default MeetingNotesDocView;
